import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    agent_name: "", agency_name: "", mobile: "", email: "", gst_number: "", password: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created. Sign in to choose your plan.");
      nav("/login", { state: { justRegistered: true, email: form.email } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr]">
      <div className="flex items-center justify-center p-8 lg:p-14 bg-[#FCFCFC]">
        <div className="w-full max-w-lg">
          <Link to="/" className="inline-flex items-center mb-10">
            <img
              src="/brand/logo.png"
              alt="Traveloble"
              className="h-60 w-auto select-none"
              draggable="false"
            />
          </Link>
          <div className="overline mb-2">Step 1 of 3 — Registration</div>
          <h1 className="font-editorial text-4xl text-slate-900">Create your agent account</h1>
          <p className="text-sm text-slate-500 mt-2">Already registered? <Link to="/login" className="text-[#16A34A] underline underline-offset-4">Sign in</Link></p>

          <form onSubmit={submit} className="mt-8 grid gap-4">
            <Field label="Agent Name" value={form.agent_name} onChange={set("agent_name")} testid="reg-agent-name" required />
            <Field label="Agency Name" value={form.agency_name} onChange={set("agency_name")} testid="reg-agency-name" required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Mobile Number" value={form.mobile} onChange={set("mobile")} testid="reg-mobile" required />
              <Field label="GST Number (optional)" value={form.gst_number} onChange={set("gst_number")} testid="reg-gst" />
            </div>
            <Field label="Email Address" type="email" value={form.email} onChange={set("email")} testid="reg-email" required />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="reg-password" required />
            <button data-testid="reg-submit-btn" type="submit" disabled={busy} className="btn-bronze mt-2 inline-flex items-center justify-center gap-2">
              {busy ? "Creating account…" : (<>Continue to payment <ArrowRight size={15} /></>)}
            </button>
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img alt="" src="https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=1600" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1E5B]/70 via-[#0F1E5B]/40 to-[#0F1E5B]/90" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <div className="overline text-[#BBF7D0]">Powered by Traveloble</div>
          <div>
            <div className="font-editorial text-5xl leading-tight">Turn inquiries into<br /><span className="italic text-[#BBF7D0]">confirmed bookings.</span></div>
            <p className="mt-5 text-white/80 max-w-md">Join hundreds of independent agents building premium quotations in minutes, not hours.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value} onChange={onChange}
             className="w-full border border-border rounded px-3.5 py-2.5 text-sm" />
    </div>
  );
}
